import { useCallback, useEffect, useRef, useState } from 'react'
import { useRole } from '../auth/useRole'
import {
  getUnreadCount,
  listNotificaciones,
  marcarNotificacionLeida,
  marcarTodasNotificacionesLeidas,
  type NotificacionInterna,
} from '../services/api'

/**
 * Estado de la campana de notificaciones internas.
 *
 * Polling manual (no hay react-query en el proyecto): cada `intervalMs` consulta
 * el contador y la lista. Solo activo si el usuario tiene algún rol interno; el
 * backend ya filtra por la audiencia del rol. El poll es best-effort (silencioso).
 */
export function useNotificaciones(intervalMs = 60_000) {
  const { roles } = useRole()
  const habilitado = roles.length > 0
  const [items, setItems] = useState<NotificacionInterna[]>([])
  const [unread, setUnread] = useState(0)
  const alive = useRef(true)

  const tick = useCallback(async () => {
    try {
      const [c, list] = await Promise.all([
        getUnreadCount(),
        listNotificaciones({ limit: 30 }),
      ])
      if (alive.current) {
        setUnread(c)
        setItems(list)
      }
    } catch {
      /* poll best-effort: silencioso */
    }
  }, [])

  useEffect(() => {
    if (!habilitado) return
    alive.current = true
    tick()
    const id = setInterval(tick, intervalMs)
    return () => {
      alive.current = false
      clearInterval(id)
    }
  }, [habilitado, intervalMs, tick])

  const marcar = useCallback(async (n: NotificacionInterna) => {
    if (n.leida) return
    // Optimista: baja el contador y marca el item; el próximo tick reconcilia.
    setUnread((u) => Math.max(0, u - 1))
    setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, leida: true } : x)))
    try {
      await marcarNotificacionLeida(n.id)
    } catch {
      /* si falla, el próximo tick reconcilia */
    }
  }, [])

  const marcarTodas = useCallback(async () => {
    setItems((prev) => prev.map((x) => ({ ...x, leida: true })))
    setUnread(0)
    try {
      await marcarTodasNotificacionesLeidas()
    } catch {
      /* el próximo tick reconcilia */
    }
  }, [])

  return { items, unread, habilitado, marcar, marcarTodas, refetch: tick }
}
